// Client-side hashed user data for Google Enhanced Conversions.
//
// Google matches EC on SHA-256 hashes of normalized PII
// (developers.google.com/google-ads/api/docs/conversions/upload-clicks):
//   email — trim + lowercase; dots and +aliases stripped from the local part
//           for gmail.com / googlemail.com ONLY (other domains keep them);
//   phone — E.164 WITH the leading '+'; bare 10-digit US numbers get the
//           country code prepended.
// Hashing happens in the browser so raw PII never enters the dataLayer;
// GTM's manual user-provided-data variable forwards the hashes (Google
// detects 64-hex values as pre-hashed). Mirrors the server-side
// normalization in peklava-infrastructure PR #1279 — keep the two in sync.

export function normalizeEmailForEc(email) {
  const em = (email || "").trim().toLowerCase();
  const at = em.indexOf("@");
  if (at <= 0 || at === em.length - 1) return "";
  let local = em.slice(0, at);
  const domain = em.slice(at + 1);
  if (domain === "gmail.com" || domain === "googlemail.com") {
    local = local.split("+")[0].replace(/\./g, "");
  }
  return local ? local + "@" + domain : "";
}

export function normalizePhoneForEc(phone) {
  let digits = (phone || "").replace(/\D/g, "");
  if (digits.length === 10) {
    digits = "1" + digits;
  }
  // E.164: country codes never start with 0.
  return digits.length >= 11 && digits[0] !== "0" ? "+" + digits : "";
}

export async function sha256Hex(value) {
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value)
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ALWAYS returns BOTH keys: { uph_em, uph_ph }, '' when a value cannot be
// produced. dataLayer v2 keys persist across pushes, so a second lead in the
// same page session must overwrite the previous lead's hashes rather than
// inherit them — gtag drops empty fields, so '' is a safe reset. Never
// throws, and a 500ms race guarantees the leadSale push can never be blocked
// by a hung WebCrypto promise.
export async function hashedEnhancedConversionData(email, phone) {
  const empty = { uph_em: "", uph_ph: "" };
  try {
    if (typeof crypto === "undefined" || !crypto.subtle) {
      return empty;
    }
    const work = (async () => {
      const out = { ...empty };
      const em = normalizeEmailForEc(email);
      if (em) {
        out.uph_em = await sha256Hex(em);
      }
      const ph = normalizePhoneForEc(phone);
      if (ph) {
        out.uph_ph = await sha256Hex(ph);
      }
      return out;
    })();
    const timeout = new Promise((resolve) => {
      setTimeout(() => resolve(empty), 500);
    });
    return await Promise.race([work, timeout]);
  } catch (e) {
    return empty;
  }
}
